/**
 * SHARED STAT LINE FORMATTER
 *
 * Produces the canonical one-line stat string used across the player screens,
 * e.g. `17 CAR, 89 RUSH YD, 4/4 REC, 12 REC YD`.
 *
 * WHY THIS IS SHARED
 * ----------------------------------------------------------------------
 * The PTS tab's `CollapsibleScoreBreakdown` had this logic inline, operating on
 * the ACTUAL scoring breakdown's nested `stat_line` objects. The Gear tab must
 * present PROJECTED stats in the *exact same format* so the two tabs read
 * identically. Duplicating the formatter would guarantee the two drift apart,
 * so the ordering/labelling rules live here once and both callers use them.
 *
 * ⚠️ TWO CALLERS, TWO KINDS OF NUMBER. Keep both in mind when editing:
 *
 *   • PTS tab — ACTUAL results. Whole events: 17 carries, 2 touchdowns.
 *   • Gear tab — PROJECTED tier AVERAGES. Fractions are normal and carry real
 *     meaning: `receiving_tds: 0.51` is "about half a touchdown per game".
 *
 * An earlier revision floored every value, which suited the first caller and
 * silently destroyed the second — sub-1.0 touchdown rates floored to 0, were
 * dropped by the zero-filter, and projected lines lost their scoring entirely.
 * Values are now carried at full precision and rounded only at render
 * (`formatStatValue()`), so integer payloads are byte-for-byte unchanged while
 * averages survive.
 *
 * INPUT SHAPE: a FLAT stats object with snake_case keys. Callers holding the
 * nested passing/rushing/receiving breakdown shape should flatten it first with
 * `combineStatGroups()` below.
 *
 * ORDERING IS PART OF THE CONTRACT. The reception fraction ("4/6 REC") belongs
 * before REC YD and REC TD, regardless of whether receiving yards are present,
 * zero, or negative. Pinned by `statLineFormat.test.js`.
 */

/**
 * Field order and labels. Order is display order and is intentional — passing,
 * then rushing, then receiving, matching how the PTS tab has always read.
 * `null` label entries are handled by bespoke logic in the formatter (the
 * paired completions/attempts and receptions/targets fractions).
 */
const STAT_DISPLAY_ORDER = [
    { key: 'passing_yards', label: 'PASS YD' },
    { key: 'passing_tds', label: 'PASS TD' },
    { key: 'interceptions', label: 'INT' },
    { key: 'carries', label: 'CAR' },
    { key: 'rushing_yards', label: 'RUSH YD' },
    { key: 'rushing_tds', label: 'RUSH TD' },
    // ⚠️ `fumbles` and `fumbles_lost` BOTH render as "FUM" and must never both
    // be emitted. The two callers name the stat differently — the scoring
    // engine's breakdown supplies `fumbles`, `PlayerTierAverages.average_stats`
    // supplies `fumbles_lost` — and a real projection payload carries BOTH, so
    // listing each here produced "0.13 FUM, 0.13 FUM" on every RB/WR line.
    // Resolved as a single entry by `resolveFumbles()`, the same
    // one-stat-two-names pattern as `resolvePassingAttempts()`.
    { key: 'receiving_yards', label: 'REC YD' },
    { key: 'receiving_tds', label: 'REC TD' },
];

/**
 * Resolve the fumble figure across the two payload shapes.
 *
 * `fumbles` (scoring engine, via `combineStatGroups()`) wins when both are
 * present, so the PTS tab's output is exactly what it always was.
 *
 * @returns {{ value: *, field: string }|null} null when NEITHER key is present.
 */
function resolveFumbles(stats) {
    if (stats.fumbles !== undefined) {
        return { value: stats.fumbles, field: 'fumbles' };
    }
    if (stats.fumbles_lost !== undefined) {
        return { value: stats.fumbles_lost, field: 'fumbles_lost' };
    }
    return null;
}

/**
 * THE TWO NUMBER MODES. Both callers share the ordering/labelling rules above
 * but emphatically DO NOT share how a number should be rendered.
 *
 *   • `'actual'`  — PTS tab. Whole events. You cannot throw 0.85 of a carry.
 *   • `'projected'` — Gear tab. Tier AVERAGES, where fractions carry the
 *     meaning (`receiving_tds: 0.51` = "about half a TD per game").
 */
export const STAT_MODE_ACTUAL = 'actual';
export const STAT_MODE_PROJECTED = 'projected';

/**
 * Coerce a stat to its displayable NUMERIC value.
 *
 * ⚠️ FLOORING IS MODE-DEPENDENT, AND BOTH BEHAVIOURS ARE LOAD-BEARING.
 * An earlier revision removed `Math.floor()` unconditionally to rescue
 * projections, and in doing so broke the PTS tab it had always served:
 *
 *   • PROJECTED (why flooring had to go): `PlayerTierAverages.average_stats`
 *     are averages, so sub-1.0 rates are normal. Flooring sent every one to 0,
 *     the `value <= 0` filter then dropped it, and a projected line lost its
 *     touchdowns entirely — understating the very thing the projection exists
 *     to show.
 *
 *   • ACTUAL (why flooring had to stay): the scoring engine's
 *     `stat_line_modified` is NOT integral. `calculation_receipt.py` stores
 *     `float_value * multiplier`, so a geared-up line legitimately holds
 *     `carries: 17.85`. The PTS tab has always floored that to `17 CAR`;
 *     without flooring it started rendering `17.85 CAR`, which reads as a
 *     nonsensical "17.85 carries actually happened".
 *
 * One global answer cannot be right for both, so the caller states which kind
 * of number it holds. Flooring happens HERE, before the zero-filter, so
 * `'actual'` reproduces the original truncate-then-filter behaviour exactly.
 *
 * NaN IS A DATA BUG AND IS RAISED LOUDLY. A silently-rendered "NaN CAR" hides a
 * broken upstream payload; throwing surfaces it at the point of corruption with
 * the offending field named.
 */
function toDisplayValue(value, fieldName, mode) {
    if (value === undefined || value === null) return 0;

    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
        throw new Error(
            `statLineFormat: NaN value found in "${fieldName}" (received ` +
            `${JSON.stringify(value)}). This is a data bug — the stat payload is corrupt.`
        );
    }

    // Actual results are whole events. Truncating BEFORE the zero-filter is
    // what the PTS tab always did, so 0.4 carries stays omitted rather than
    // appearing as "0.4 CAR".
    return mode === STAT_MODE_ACTUAL ? Math.floor(numeric) : numeric;
}

/**
 * Render a stat value: whole numbers bare, fractions to two decimals.
 *
 * `17` → `"17"` (PTS tab — always integral by the time it arrives here,
 *                 because `'actual'` floored it in `toDisplayValue()`)
 * `0.51` → `"0.51"` (projected average — the figure that used to vanish)
 * `74.8` → `"74.8"` (trailing zeros trimmed, so never "74.80")
 *
 * Two decimals matches the precision `PlayerTierAverages` actually stores
 * (`round(avg_value, 2)` in `ProjectionAnalysisService._calculate_averages`),
 * so nothing is invented and nothing meaningful is lost.
 */
function formatStatValue(value) {
    if (Number.isInteger(value)) return String(value);

    // parseFloat drops trailing zeros: 74.80 → "74.8", 0.50 → "0.5".
    return String(parseFloat(value.toFixed(2)));
}

/**
 * Flatten the nested passing/rushing/receiving breakdown shape into the flat
 * dict this module formats. Used by the PTS tab, whose scoring breakdown nests
 * stats by category. Projection payloads are already flat and skip this.
 */
export function combineStatGroups({ passing, rushing, receiving } = {}) {
    const combined = {};

    const copyPresent = (source, keys) => {
        if (!source) return;
        keys.forEach((key) => {
            if (source[key] !== undefined) combined[key] = source[key];
        });
    };

    copyPresent(passing, [
        'completions', 'attempts', 'passing_yards', 'passing_tds', 'interceptions',
    ]);
    copyPresent(rushing, ['carries', 'rushing_yards', 'rushing_tds', 'fumbles']);
    copyPresent(receiving, [
        'receptions', 'targets', 'receiving_yards', 'receiving_tds',
    ]);

    return combined;
}

/**
 * Resolve the pass-attempts figure across the two REAL payload shapes.
 *
 * WHY THIS EXISTS — a genuine bug this fixed:
 * ----------------------------------------------------------------------
 * The two callers name this stat differently, and the formatter previously
 * only knew one of them:
 *
 *   • PTS tab (`CollapsibleScoreBreakdown`) — the scoring engine's nested
 *     breakdown, flattened by `combineStatGroups()`, supplies `attempts`.
 *   • Gear tab projections — `PlayerTierAverages.average_stats` supplies
 *     `passing_attempts`.
 *
 * Because only `stats.attempts` was checked, real projection payloads failed
 * the presence test and the `N CMP` segment NEVER rendered for projected QBs.
 * Both key names are now accepted. `attempts` wins when both are present so the
 * PTS tab's behaviour is bit-for-bit what it always was.
 *
 * @returns {{ value: *, field: string }|null} null when NEITHER key is present.
 */
function resolvePassingAttempts(stats) {
    if (stats.attempts !== undefined) {
        return { value: stats.attempts, field: 'attempts' };
    }
    if (stats.passing_attempts !== undefined) {
        return { value: stats.passing_attempts, field: 'passing_attempts' };
    }
    return null;
}

/**
 * Format a flat stats dict into the canonical stat line.
 *
 * @param {Object} stats - flat snake_case stat dict
 * @returns {string} e.g. `17 CAR, 89 RUSH YD, 4/4 REC` — empty string when the
 *          player has no displayable production.
 *
 * ZERO-VALUE FIELDS ARE OMITTED, not printed as "0 PASS TD". A stat line is a
 * summary of what happened, and padding it with zeroes for every category the
 * player never touched makes a QB's line unreadable. Absent stats are likewise
 * omitted rather than invented as 0 — consistent with the projection contract's
 * "show the fields that exist, hide the rest" rule.
 *
 * @param {Object} stats - flat snake_case stat dict
 * @param {'actual'|'projected'} [mode] - how to render the numbers. Defaults to
 *        ACTUAL (whole events), because that is the pre-existing PTS behaviour
 *        and a caller that has not thought about it should not silently start
 *        printing "17.85 CAR". The Gear tab passes `'projected'` explicitly.
 */
export function formatStatLine(stats, mode = STAT_MODE_ACTUAL) {
    if (!stats || typeof stats !== 'object') return '';

    const parts = [];

    // COMPLETIONS PRINT ALONE ("22 CMP"), NOT AS A FRACTION.
    // This is the PTS tab's long-standing format and is reproduced verbatim so
    // extracting this helper changed no existing output. Attempts are still
    // required to be present — a completions figure with no attempts alongside
    // it indicates a partial payload — but only completions is displayed.
    // Attempts may arrive as `attempts` (scoring engine) or `passing_attempts`
    // (projection tier averages); see `resolvePassingAttempts()`.
    const attemptsEntry = stats.completions !== undefined
        ? resolvePassingAttempts(stats)
        : null;

    if (attemptsEntry) {
        const completions = toDisplayValue(stats.completions, 'completions', mode);
        const attempts = toDisplayValue(attemptsEntry.value, attemptsEntry.field, mode);
        if (completions > 0 || attempts > 0) {
            parts.push(`${formatStatValue(completions)} CMP`);
        }
    }

    STAT_DISPLAY_ORDER.forEach(({ key, label }) => {
        // ⚠️ THE RECEPTION FRACTION IS INSERTED BEFORE THE ZERO/ABSENT FILTER.
        //
        // "4/6 REC" belongs immediately ahead of receiving yards and therefore
        // ahead of REC TD, which is the order the PTS tab has always used.
        // Doing this after the `value <= 0` guard below meant that a payload
        // with receptions and a receiving TD but zero/absent receiving YARDS
        // skipped the insertion, fell through to the trailing call, and emitted
        // "1 REC TD, 3/4 REC" — receptions AFTER the touchdown they produced.
        //
        // That is a real stat line (a short scoring catch), and the inversion
        // would have hit the PTS tab too, so it is a regression in existing
        // behaviour rather than a projection-only quirk. Anchoring on the KEY
        // (not its value) keeps the position stable however receiving yards
        // turn out.
        if (key === 'receiving_yards') {
            appendReceptions(parts, stats, mode);
        }

        // ⚠️ NO EARLY `return` HERE — IT WOULD SKIP THE FUMBLE HOOK BELOW.
        //
        // This block used to `return` when the key was absent or its value
        // non-positive, which took the whole iteration with it. For
        // `rushing_tds` that meant a player who fumbled but did NOT score on
        // the ground — an extremely ordinary line — never reached
        // `appendFumbles()`. FUM then fell through to the trailing fallback and
        // printed AFTER the receiving stats:
        //   `17 CAR, 89 RUSH YD, 4/4 REC, 12 REC YD, 1 FUM`
        // instead of its long-standing position between the rushing and
        // receiving groups.
        //
        // Both tabs share this formatter, so that misordering hit the shipped
        // PTS display too. Guarding with `if` rather than `return` lets the
        // stat be skipped while the iteration continues to its hooks.
        if (stats[key] !== undefined) {
            const value = toDisplayValue(stats[key], key, mode);
            if (value > 0) {
                parts.push(`${formatStatValue(value)} ${label}`);
            }
        }

        // FUM sits at the END of the rushing group — after RUSH TD when there
        // is one, and in exactly the same slot when there is not. Anchored on
        // the KEY (as the reception fraction above is), so its position is
        // stable however `rushing_tds` turns out. `STAT_DISPLAY_ORDER` is
        // walked in full regardless of the payload, so this is reached even
        // when the player has no rushing keys at all.
        if (key === 'rushing_tds') {
            appendFumbles(parts, stats, mode);
        }
    });

    // `rushing_tds` may be absent from the payload entirely (a pure receiver),
    // in which case the walk above never reached the fumble hook.
    appendFumbles(parts, stats, mode);

    // Receiving yards absent from STAT_DISPLAY_ORDER's walk entirely (the key
    // is missing from the payload) still needs the fraction to appear.
    appendReceptions(parts, stats, mode);

    return parts.join(', ');
}

/**
 * Push the "N FUM" segment onto `parts`, at most once per stat line.
 *
 * Guards on the rendered label rather than a flag so that both call sites (the
 * ordered walk and the trailing fallback) are naturally idempotent — the same
 * shape `appendReceptions()` uses.
 */
function appendFumbles(parts, stats, mode) {
    if (parts.some((part) => part.endsWith(' FUM'))) return;

    const entry = resolveFumbles(stats);
    if (!entry) return;

    const value = toDisplayValue(entry.value, entry.field, mode);
    if (value <= 0) return;

    parts.push(`${formatStatValue(value)} FUM`);
}

/**
 * Push the "4/6 REC" fraction onto `parts`, at most once per stat line.
 *
 * Both sides go through `formatStatValue()` so a projected average renders as
 * "3.67/9.3 REC" rather than dumping raw floats like "3.67/9.300000000000001".
 */
function appendReceptions(parts, stats, mode) {
    if (parts.some((part) => part.endsWith(' REC'))) return;
    if (stats.receptions === undefined || stats.targets === undefined) return;

    const receptions = toDisplayValue(stats.receptions, 'receptions', mode);
    const targets = toDisplayValue(stats.targets, 'targets', mode);

    if (receptions > 0 || targets > 0) {
        parts.push(
            `${formatStatValue(receptions)}/${formatStatValue(targets)} REC`
        );
    }
}

/**
 * Format a point value for display, preserving the null/zero distinction.
 *
 * @param {number|null|undefined} points
 * @returns {string} `"11.00pts"`, or `"—"` when the value is genuinely unknown.
 *
 * A real 0 prints as "0.00pts" because a zero projection is a finding. Only
 * null/undefined — meaning "we have no data" — becomes the em dash.
 */
export function formatPoints(points) {
    if (points === null || points === undefined) return '—';

    const numeric = Number(points);
    if (Number.isNaN(numeric)) {
        throw new Error(
            `statLineFormat: NaN point value (received ${JSON.stringify(points)}). ` +
            'This is a data bug — refusing to render a corrupt score.'
        );
    }

    return `${numeric.toFixed(2)}pts`;
}

/**
 * Format a signed delta, e.g. `+6.00pts` / `-1.50pts` / `—`.
 * Used for gear adjustments, where the sign carries the meaning.
 */
export function formatPointsDelta(points) {
    if (points === null || points === undefined) return '—';

    const numeric = Number(points);
    if (Number.isNaN(numeric)) {
        throw new Error(
            `statLineFormat: NaN delta value (received ${JSON.stringify(points)}). ` +
            'This is a data bug — refusing to render a corrupt delta.'
        );
    }

    const sign = numeric >= 0 ? '+' : '';
    return `${sign}${numeric.toFixed(2)}pts`;
}
